<script setup>
  import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
  import { faGamepad } from "@fortawesome/free-solid-svg-icons"
  import { faTableList } from "@fortawesome/free-solid-svg-icons"
  import { faUserGear } from "@fortawesome/free-solid-svg-icons"
  import { faMoneyBill1 } from "@fortawesome/free-solid-svg-icons"
  import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons"
  import { faVideo } from "@fortawesome/free-solid-svg-icons"
  import { faUser } from "@fortawesome/free-solid-svg-icons"

  import { onBeforeRouteLeave, RouterLink } from 'vue-router'

  import { useAuthStore } from "@/stores/auth.js"
  import { useAvailablePurchasesStore } from "@/stores/availablePurchasesStore.js"
  import { useDeviceStore } from "@/stores/deviceStore.js";
  import { useEnvironmentStore } from "@/stores/environmentStore.js";
  import { getCameraStreamByLabel } from "@/plugins/camera.js"

  import { computed, onMounted, ref } from "vue"

  const authStore = useAuthStore()
  const availablePurchasesStore = useAvailablePurchasesStore()
  const deviceStore = useDeviceStore()
  const environmentStore = useEnvironmentStore()

  const loaded = ref( false )

  const video = ref( null )
  const selectedMediaDevice = ref( null )

  onMounted( async () => {
    console.log( "onMounted called" )
    if ( authStore.isLoggedIn ) {
      availablePurchasesStore
        .getAvailablePurchases( false )
        .then( ( response ) => {
          loaded.value = true
        } )
    }
    else if ( deviceStore.device ) {
      selectedMediaDevice.value = await getCameraStreamByLabel( deviceStore.device.device_name )
    }
  } )

  deviceStore.$subscribe( async ( mutation, state ) => {
    if ( !selectedMediaDevice.value && deviceStore.device && !authStore.isLoggedIn ) {
      selectedMediaDevice.value = await getCameraStreamByLabel( deviceStore.device.device_name )
    }
  } );

  onBeforeRouteLeave( () => {
    turnOffCamera()
  })

  function turnOffCamera() {
    if ( video.value ) {
      video.value.pause()
      video.value.srcObject = null
    }

    if ( selectedMediaDevice.value ) {
      selectedMediaDevice.value.video.getTracks().forEach( ( track ) => {
        track.stop()
      } )
    }
  }

  const needPurchase = computed( () => {
    return availablePurchasesStore.applicablePurchase === null
        && !availablePurchasesStore.useFreeGame
  })
</script>

<template lang="pug">
  .card.opacity-85( v-if="authStore.isLoggedIn" )
    .card-body

      // Staging Warning
      .alert.alert-warning( v-if="!deviceStore.device && !environmentStore.isProduction()" )
        font-awesome-icon.me-1( :icon="faTriangleExclamation" )
        | If this was a production UCUE device the "Create Game" Link would not be active. However, that would make
        | testing very difficult, so, it is only disabled for our "live" production environment.

      .alert.alert-warning( v-if="availablePurchasesStore.useFreeGame" )
        p
          font-awesome-icon.me-1( :icon="faTriangleExclamation" )
          | You only have the
          |
          u one free game
          |
          | from signing up for UCUE!
        RouterLink.btn.btn-primary( :to="{ name: 'prices' }" )
          font-awesome-icon.me-1( :icon="faMoneyBill1" )
          | Consider purchasing a subscription or a game now!

      .alert.alert-warning( v-else-if="availablePurchasesStore.applicablePurchase === null" )
        p
          font-awesome-icon.me-1( :icon="faTriangleExclamation" )
          | You do not have any games or subscriptions active right now, so you cannot play any games!
        RouterLink.btn.btn-primary( :to="{ name: 'prices' }" )
          font-awesome-icon.me-1( :icon="faMoneyBill1" )
          | Purchase a subscription or a game now!

      .row
        .col-lg-6.mb-3
          .card.border-secondary.p-3.h-100.w-100.d-flex.justify-content-center( v-if="!deviceStore.enableGame")
            .row
              .col-lg-12
                .lead
                  font-awesome-icon.me-1( :icon="faGamepad" )
                  | Create Game
            .row.mt-3
              .col-lg-12
                .alert.alert-warning( v-if="needPurchase" )
                  font-awesome-icon.me-1( :icon="faTriangleExclamation" )
                  | You must purchase a game or a subscription to Create a Game!

            .row
              .col-lg-12
                .alert.alert-warning.mb-0( v-if="!deviceStore.device" )
                  font-awesome-icon.me-1( :icon="faTriangleExclamation" )
                  | You can only play a game on an official UCUE table!
                  // TODO: Add locations for pool halls

          RouterLink.btn.btn-lg.btn-outline-light.btn-square.p-3( v-else :to="{ name: 'createGame' }")
            .d-flex
              .flex-column
                .col-lg-12
                  font-awesome-icon.me-1( :icon="faGamepad" )
                  | Create Game

                  .alert.alert-warning.mb-0.mt-3( v-if="!deviceStore.device" )
                    font-awesome-icon.me-1( :icon="faTriangleExclamation" )
                    | You can only play a game on an official UCUE table!
                    // TODO: Add locations for pool halls

        .col-lg-6.mb-3
          RouterLink.btn.btn-lg.btn-outline-light.btn-square.p-3( :to="{ name: 'viewGames' }" )
            font-awesome-icon.me-1( :icon="faTableList" )
            | View Previous Games
      .row
        .col-lg-6.mb-3.mb-lg-0
          RouterLink.btn.btn-lg.btn-outline-light.btn-square.p-3( :to="{ name: 'prices' }" )
            font-awesome-icon.me-1( :icon="faMoneyBill1" )
            | Purchase Game/Subscription
        .col-lg-6
          RouterLink.btn.btn-lg.btn-outline-light.btn-square.p-3( :to="{ name: 'modifyAccount' }" )
            font-awesome-icon.me-1( :icon="faUserGear" )
            | Modify Account

  .card.opacity-100.text-start( v-else-if="selectedMediaDevice" )
    .card-body.pt-1.ps-1.pe-1.pb-0
      .row
        .col-12
          .h-100.w-100
            video.ratio.object-fit-cover( ref="video"
                                          :srcObject="selectedMediaDevice.video"
                                          autoplay
                                          muted
                                          :style="`--bs-aspect-ratio: ${selectedMediaDevice.videoSettings?.aspectRatio};`" )
          .floating-alert
            .alert.alert-success.mb-2.text-center.p-2.opacity-85
              .row
                .col-md-6
                  .display-7.fw-bold.mb-0.mt-0.text-decoration-underline
                    font-awesome-icon.me-3( :icon="faVideo" )
                    | First Game is Free!

                .col-md-6
                  RouterLink.btn.btn-primary.btn-sm.ms-2( :to="{ name: 'login' }" )
                    font-awesome-icon.me-1( :icon="faUser" )
                    | Login or Create an Account for Free!

</template>

<style scoped lang="scss">
  .btn-square {
    width: 100%; /* Make buttons take full width of the column */
    padding: 50px; /* Adjust padding for larger button area */
    height: 250px;
    font-size: 1.5rem; /* Adjust font size for visibility */
    display: flex; /* Use flexbox */
    align-items: center; /* Center vertically */
    justify-content: center; /* Center horizontally */
  }

  .display-7 {
    font-size: 110% !important;
  }

  .floating-alert {
    position: absolute; /* Or fixed, depending on your needs */
    top: 20px; /* Adjust as needed */
    left: 50%;
    transform: translateX(-50%); /* Center the alert */
    z-index: 1050; /* Ensure it's above the videos */
    width: 97%; /* Or a specific width */
  }
</style>
